import { useState } from 'react'
import { callEdgeFunction } from '@/lib/edge-functions'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { MoreHorizontal, Pencil, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { AdminUser, AdminRole } from '@/types/database'

interface TeamMemberActionsProps {
  member: AdminUser
  currentUserRole: AdminRole
}

export function TeamMemberActions({ member, currentUserRole, onUpdate }: TeamMemberActionsProps & { onUpdate?: () => void }) {
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<AdminRole>(member.role)
  const [isLoading, setIsLoading] = useState(false)

  const isOwner = currentUserRole === 'owner'
  const isAdmin = currentUserRole === 'admin'
  const canChangeRole = isOwner
  const canRemove = isOwner ? member.role !== 'owner' : isAdmin ? member.role === 'editor' : false
  const hasAnyActions = canChangeRole || canRemove

  // Determine which roles can be assigned
  const availableRoles: { value: AdminRole; label: string }[] = []
  if (isOwner) {
    availableRoles.push({ value: 'owner', label: 'Owner' })
    availableRoles.push({ value: 'admin', label: 'Admin' })
    availableRoles.push({ value: 'editor', label: 'Editor' })
  } else if (currentUserRole === 'admin') {
    availableRoles.push({ value: 'editor', label: 'Editor' })
  }

  const openRoleDialog = () => {
    setSelectedRole(member.role)
    setIsRoleDialogOpen(true)
  }

  const handleChangeRole = async () => {
    if (selectedRole === member.role) {
      setIsRoleDialogOpen(false)
      return
    }

    setIsLoading(true)

    try {
      const result = await callEdgeFunction('admin-team', {
        method: 'POST',
        body: { action: 'update-role', memberId: member.id, role: selectedRole },
        withAuth: true,
      })

      if (result.error) {
        throw new Error(result.error || 'Failed to update role')
      }

      toast.success('Role updated successfully')
      onUpdate?.()
      setIsRoleDialogOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update role')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemove = async () => {
    if (!canRemove) {
      toast.error(member.role === 'owner' ? 'Owner users cannot be removed' : 'Insufficient permissions')
      setIsDeleteDialogOpen(false)
      return
    }

    setIsLoading(true)

    try {
      const result = await callEdgeFunction('admin-team', {
        method: 'POST',
        body: { action: 'remove', memberId: member.id },
        withAuth: true,
      })

      if (result.error) {
        throw new Error(result.error || 'Failed to remove team member')
      }

      toast.success('Team member removed')
      onUpdate?.()
      setIsDeleteDialogOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove team member')
    } finally {
      setIsLoading(false)
    }
  }

  if (!hasAnyActions) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canChangeRole && (
            <DropdownMenuItem onClick={openRoleDialog}>
              <Pencil className="mr-2 h-4 w-4" />
              Change Role
            </DropdownMenuItem>
          )}
          {canChangeRole && canRemove && <DropdownMenuSeparator />}
          {canRemove && (
            <DropdownMenuItem
              onClick={() => setIsDeleteDialogOpen(true)}
              className="text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Change Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update the role for {member.name || member.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as AdminRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRoleDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleChangeRole} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Team Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {member.name || member.email} from the team?
              They will lose all admin access immediately.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemove}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
